import { Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { StructuredData, buildFullUrl, getImageUrl } from './structured-data';
import { ProductData } from '../product/product-types';

type ProductSchemaProps = {
    product: ProductData;
};

/**
 * MedicalDevice schema for product pages on Atos Medical sites.
 *
 * Note: We previously included a Product schema, but Google requires Product schemas
 * to have either "offers", "review", or "aggregateRating" for rich results eligibility.
 * Since this is an informational medical device site (not e-commerce), we only use
 * the MedicalDevice schema which is more semantically appropriate and doesn't have
 * these requirements.
 *
 * For Atos Care (e-commerce), no product schema is rendered as they would also
 * need offers/pricing data to satisfy Google's requirements.
 */
export const ProductSchema = (props: ProductSchemaProps) => {
    const { siteInfo } = useContext(AppContext);

    const isAtosCare = siteInfo.siteType === 'atos-care';
    const productUrl = buildFullUrl('https', siteInfo.currentDomain, props.product.permalink || props.product.url);

    const productImage = () => {
        // Check featured images first
        const featuredImage = props.product.images?.find(img => img.isFeatured && img.file?.url);
        if (featuredImage?.file?.url) return featuredImage.file.url;

        // Then any image
        const anyImage = props.product.images?.find(img => img.file?.url);
        if (anyImage?.file?.url) return anyImage.file.url;

        // Fallback to OG image
        if (props.product.seo?.og_image) {
            return getImageUrl(props.product.seo.og_image);
        }

        return undefined;
    };

    const description = () => {
        return props.product.shortDescription
            || props.product.seo?.metaDescription
            || props.product.longDescription?.substring(0, 200);
    };

    const medicalDeviceSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'MedicalDevice',
        name: props.product.post_title,
        url: productUrl,
        ...(description() && { description: description() }),
        ...(productImage() && { image: productImage() }),
    });

    // Only render MedicalDevice schema for Atos Medical (not Atos Care)
    return (
        <Show when={props.product?.post_title && !isAtosCare}>
            <StructuredData data={medicalDeviceSchema()} />
        </Show>
    );
};
